import React from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Filter, X, MapPin, Image, TrendingUp, CheckCircle, XCircle, Clock, Eye } from 'lucide-react';

export type TargetTypeFilter = 'location' | 'image' | 'prediction';
export type StatusFilter = 'unresolved' | 'correct' | 'incorrect' | 'completed' | 'in_progress';

interface HistoryFiltersProps {
  typeFilters: Set<TargetTypeFilter>;
  statusFilters: Set<StatusFilter>;
  onTypeFilterChange: (types: Set<TargetTypeFilter>) => void;
  onStatusFilterChange: (statuses: Set<StatusFilter>) => void;
}

const TYPE_OPTIONS: { value: TargetTypeFilter; label: string; icon: React.ReactNode }[] = [
  { value: 'location', label: 'Location', icon: <MapPin className="h-4 w-4" /> },
  { value: 'image', label: 'Image', icon: <Image className="h-4 w-4" /> },
  { value: 'prediction', label: 'Prediction', icon: <TrendingUp className="h-4 w-4" /> },
];

const STATUS_OPTIONS: { value: StatusFilter; label: string; icon: React.ReactNode }[] = [
  { value: 'in_progress', label: 'In Progress', icon: <Clock className="h-4 w-4" /> },
  { value: 'completed', label: 'Completed', icon: <Eye className="h-4 w-4" /> },
  { value: 'unresolved', label: 'Unresolved', icon: <Clock className="h-4 w-4" /> },
  { value: 'correct', label: 'Correct', icon: <CheckCircle className="h-4 w-4" /> },
  { value: 'incorrect', label: 'Incorrect', icon: <XCircle className="h-4 w-4" /> },
];

export const HistoryFilters: React.FC<HistoryFiltersProps> = ({
  typeFilters,
  statusFilters,
  onTypeFilterChange,
  onStatusFilterChange,
}) => {
  const handleTypeToggle = (type: TargetTypeFilter) => {
    const newFilters = new Set(typeFilters);
    if (newFilters.has(type)) {
      newFilters.delete(type);
    } else {
      newFilters.add(type);
    }
    onTypeFilterChange(newFilters);
  };

  const handleStatusToggle = (status: StatusFilter) => {
    const newFilters = new Set(statusFilters);
    if (newFilters.has(status)) {
      newFilters.delete(status);
    } else {
      newFilters.add(status);
    }
    onStatusFilterChange(newFilters);
  };

  const activeTypeCount = 3 - typeFilters.size;
  const activeStatusCount = 5 - statusFilters.size;
  const hasActiveFilters = activeTypeCount > 0 || activeStatusCount > 0;

  const clearAllFilters = () => {
    onTypeFilterChange(new Set(['location', 'image', 'prediction']));
    onStatusFilterChange(new Set(['unresolved', 'correct', 'incorrect', 'completed', 'in_progress']));
  };

  return (
    <div className="flex items-center gap-3 mb-4">
      <Filter className="h-4 w-4 text-muted-foreground" />
      
      {/* Type Filter */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8">
            Type
            {activeTypeCount > 0 && (
              <Badge variant="secondary" className="ml-2 h-5 px-1.5 text-xs">
                {typeFilters.size}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-2" align="start">
          <div className="space-y-2">
            {TYPE_OPTIONS.map((option) => (
              <label
                key={option.value}
                className="flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-muted"
              >
                <Checkbox
                  checked={typeFilters.has(option.value)}
                  onCheckedChange={() => handleTypeToggle(option.value)}
                />
                {option.icon}
                <span className="text-sm">{option.label}</span>
              </label>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      {/* Status Filter */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8">
            Status
            {activeStatusCount > 0 && (
              <Badge variant="secondary" className="ml-2 h-5 px-1.5 text-xs">
                {statusFilters.size}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-2" align="start">
          <div className="space-y-2">
            {STATUS_OPTIONS.map((option) => (
              <label
                key={option.value}
                className="flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-muted"
              >
                <Checkbox
                  checked={statusFilters.has(option.value)}
                  onCheckedChange={() => handleStatusToggle(option.value)}
                />
                {option.icon}
                <span className="text-sm">{option.label}</span>
              </label>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      {/* Clear Filters */}
      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={clearAllFilters}
          className="h-8 text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4 mr-1" />
          Clear
        </Button>
      )}
    </div>
  );
};

export default HistoryFilters;
